/**
 * PRV-008: Modal para Solicitar Edición de Factibilidad
 *
 * Permite al asesor solicitar edición de materiales/equipos de una factibilidad aprobada.
 * La solicitud será enviada al supervisor comercial para su aprobación.
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  Divider
} from '@mui/material';
import { useState } from 'react';
import { useFetchPostSolicitudEdicion } from '@/shared/actions/erp/solicitud_edicion_factibilidad';
import { IFactibilidadCorporativo } from '@/shared/interfaces/erp/factibilidad_corporativo';

interface ModalSolicitudEdicionProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  factibilidad: IFactibilidadCorporativo | null;
  datosCliente?: { razon_social?: string; identificacion?: string } | null;
}

export function ModalSolicitudEdicion({
  open,
  setOpen,
  factibilidad,
  datosCliente
}: ModalSolicitudEdicionProps) {
  const [motivo, setMotivo] = useState('');

  const { mutate: crearSolicitud, isPending } = useFetchPostSolicitudEdicion({
    enableSuccessAlert: true,
    enableRedirect: false
  });

  const handleSubmit = () => {
    if (!motivo.trim()) {
      return;
    }

    if (!factibilidad) {
      return;
    }

    crearSolicitud({
      factibilidad_corporativa: factibilidad.codigo,
      motivo: motivo.trim(),
    }, {
      onSuccess: () => {
        setMotivo('');
        setOpen(false);
      }
    });
  };

  const handleClose = () => {
    if (isPending) return;
    setMotivo('');
    setOpen(false);
  };

  if (!factibilidad) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        Solicitar Edición de Factibilidad
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta solicitud ser&aacute; enviada al <strong>Supervisor Comercial</strong> para su aprobaci&oacute;n.
            Una vez aprobada, la factibilidad regresar&aacute; al m&oacute;dulo de Factibilidad Corporativa
            en estado <strong>PENDIENTE</strong> para que pueda modificar los equipos, materiales y componentes.
          </Alert>

          <Divider sx={{ my: 2 }} />

          {/* Informaci&oacute;n de la Factibilidad */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Informaci&oacute;n de la Factibilidad
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                C&oacute;digo
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {factibilidad.codigo}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Estado
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {factibilidad.estado_factibilidad}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {datosCliente?.razon_social || 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Identificaci&oacute;n
              </Typography>
              <Typography variant="body1">
                {datosCliente?.identificacion || 'N/A'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Campo de motivo */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Motivo de la solicitud de Edición *"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Explique brevemente por qu&eacute; necesita editar los materiales, equipos o componentes de esta factibilidad..."
            helperText="Este motivo ser&aacute; visible para el supervisor. M&iacute;nimo 10 caracteres."
            error={motivo.length > 0 && motivo.length < 10}
            disabled={isPending}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isPending}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isPending || motivo.trim().length < 10}
        >
          {isPending ? 'Enviando...' : 'Enviar Solicitud'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModalSolicitudEdicion;
